<template>
  <div
    ref="containerRef"
    class="graph-container"
    :class="{ 'edit-mode': currentEditMode, 'theme-dark': themeMode === 'dark' }"
    @contextmenu.prevent
    @click="handleCanvasClick"
  >
    <!-- 图谱渲染层（与菜单/工具栏分离，避免切换引擎时误清 UI） -->
    <div ref="canvasRef" class="graph-canvas"></div>

    <!-- 左侧悬浮编辑工具栏 -->
    <GraphEditorToolbar
      v-if="currentEditMode && renderEngine === 'g6'"
      v-model:activeTool="currentToolType"
      :isEditing="currentEditMode"
      :theme="themeMode"
      @tool-change="onToolChange"
    />
    <!-- 右键节点菜单 -->
    <div
      v-if="nodeMenu.visible"
      class="context-menu"
      :style="{ left: nodeMenu.x + 'px', top: nodeMenu.y + 'px' }"
      @click.stop
    >
      <div class="context-menu-item" @click="handleRename">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
        <span>修名</span>
      </div>
      <div class="context-menu-item" @click="handleChangeColor">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="13.5" cy="6.5" r="4.5"/>
          <circle cx="17.5" cy="17.5" r="4.5"/>
          <circle cx="6.5" cy="17.5" r="4.5"/>
        </svg>
        <span>变更颜色</span>
      </div>
      <div class="context-menu-item" @click="handleMenuCreateLink">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="5" y1="12" x2="19" y2="12"/>
          <polyline points="12 5 19 12 12 19"/>
        </svg>
        <span>创建连接</span>
      </div>
      <div class="context-menu-item danger" @click="handleMenuDeleteNode">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="3 6 5 6 21 6"/>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
        </svg>
        <span>删除节点</span>
      </div>
    </div>

    <!-- 右键边菜单 -->
    <div
      v-if="edgeMenu.visible"
      class="context-menu"
      :style="{ left: edgeMenu.x + 'px', top: edgeMenu.y + 'px' }"
      @click.stop
    >
      <div class="context-menu-item" @click="handleEditEdgeLabel">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
        </svg>
        <span>改关系</span>
      </div>
      <div class="context-menu-item" @click="handleReverseEdge">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="17 1 21 5 17 9"/>
          <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
          <polyline points="7 23 3 19 7 15"/>
          <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
        </svg>
        <span>反转关系</span>
      </div>
      <div class="context-menu-item danger" @click="handleMenuDeleteEdge">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <polyline points="3 6 5 6 21 6"/>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
        </svg>
        <span>删除关系</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch, nextTick, provide } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import type { GraphNode, GraphEdge } from '../@types'
import { KnowledgeGraph, type GraphTheme as G6GraphTheme } from '../module/g6Graph'
import { VisKnowledgeGraph, type ToolType as VisToolType, type GraphTheme as VisGraphTheme } from '../module/VisNetwork'
import { useGraphEditor } from '../composables/useGraphEditor'
import GraphEditorToolbar from './GraphEditorToolbar.vue'
import type {
  GraphEditorOptions,
  BatchUpdateRequest,
  BatchUpdateResponse,
  GraphEditor
} from '../composables/useGraphEditor'
import type { ToolType } from '../module/g6Graph'

// ============================================================
// Props
// ============================================================

interface EditorProps {
  /** 批量更新 API（传入后 GraphVisualization 内部接管编辑逻辑） */
  api?: GraphEditorOptions['api']
  /** 保存时合并进请求（如 file_source、userId、project）；用函数以便读取最新筛选条件 */
  batchExtra?: GraphEditorOptions['batchExtra']
  /** 保存成功回调 */
  onSaveSuccess?: (stats: NonNullable<BatchUpdateResponse['stats']>) => void
  /** 保存失败回调 */
  onSaveError?: (error: Error) => void
}

interface Props {
  nodes: GraphNode[]
  edges: GraphEdge[]
  width?: number
  height?: number
  layoutType?: 'force' | 'circular'
  /** 渲染引擎（g6 / vis） */
  renderEngine?: 'g6' | 'vis'
  /** 主题（light / dark） */
  theme?: 'light' | 'dark'
  /** 编辑器配置（传入后组件内部管理编辑状态，工具栏事件自动处理） */
  editor?: EditorProps
}

const props = withDefaults(defineProps<Props>(), {
  width: 0,
  height: 0,
  layoutType: 'force',
  renderEngine: 'vis',
  theme: 'light'
})

const renderEngine = computed(() => props.renderEngine)
const themeMode = computed(() => props.theme ?? 'light')

const emit = defineEmits<{
  /** 编辑状态切换完成（仅 editor 未传入时由外部管理时使用） */
  'update:editMode': [value: boolean]
  /** 编辑状态变化（无论是否传入 editor，都向外通知，便于工具栏稳定显示） */
  'edit-mode-change': [value: boolean]
  /** 点击保存（仅 editor 未传入时） */
  save: []
  /** 点击取消（仅 editor 未传入时） */
  cancel: []
  /** 图谱已更新（外部可重新获取最新数据） */
  'graph-updated': [nodes: GraphNode[], edges: GraphEdge[]]
}>()

// ============================================================
// 编辑器（当 editor 配置传入时内部托管）
// ============================================================

const editor = props.editor
  ? useGraphEditor({
      api: props.editor.api,
      batchExtra: props.editor.batchExtra,
      onSaveSuccess: (stats) => props.editor!.onSaveSuccess?.(stats),
      onSaveError: (err) => props.editor!.onSaveError?.(err)
    })
  : null

/** 当前是否为编辑模式 */
const currentEditMode = computed(() => editor?.isEditing.value ?? false)
const currentHasChanges = computed(() => editor?.hasChanges.value ?? false)

// watch editMode → 同步到图谱
watch(currentEditMode, (newMode) => {
  emit('edit-mode-change', newMode)
  if (renderEngine.value === 'g6' && graph) {
    graph.setEditMode(newMode)
  } else if (renderEngine.value === 'vis' && visGraph) {
    visGraph.setEditMode(newMode)
  }
  if (!newMode) {
    nodeMenu.value.visible = false
    edgeMenu.value.visible = false
    currentToolType.value = 'select'
  }
})

// watch nodes/edges 变化 → emit 通知外部（用于刷新数据）
watch(
  () => [props.nodes, props.edges],
  ([newNodes, newEdges]) => {
    emit('graph-updated', newNodes as GraphNode[], newEdges as GraphEdge[])
  }
)

const containerRef = ref<HTMLElement | null>(null)
const canvasRef = ref<HTMLElement | null>(null)
let graph: KnowledgeGraph | null = null
let visGraph: VisKnowledgeGraph | null = null
let resizeObserver: ResizeObserver | null = null
let resizeTimeout: ReturnType<typeof setTimeout> | null = null

// ========== 右键菜单状态 ==========
const nodeMenu = ref({ visible: false, x: 0, y: 0, nodeId: '', label: '', color: '' })
const edgeMenu = ref({ visible: false, x: 0, y: 0, edgeId: '', sourceId: '', targetId: '', label: '' })

// ========== 工具栏状态 ==========
/** 当前选中的编辑工具类型 */
const currentToolType = ref<ToolType>('select')

// watch 工具类型变化 → 同步到图谱
watch(currentToolType, (type) => {
  if (renderEngine.value === 'g6' && graph) {
    graph.setToolType(type)
  } else if (renderEngine.value === 'vis' && visGraph) {
    visGraph.setToolType(type as VisToolType)
  }
})

const getContainerSize = () => {
  if (!containerRef.value) return { width: 0, height: 0 }
  return {
    width: containerRef.value.clientWidth,
    height: containerRef.value.clientHeight
  }
}

const initGraph = () => {
  if (!containerRef.value || !canvasRef.value) return

  if (containerRef.value.clientHeight === 0) {
    containerRef.value.style.height = '100%'
  }

  nextTick(() => {
    const { width, height } = getContainerSize()
    if (width === 0 || height === 0) return

    // 切换引擎时清空渲染层，避免残留 DOM
    canvasRef.value!.innerHTML = ''
    try {
      graph?.destroy()
    } catch {
      // ignore
    }
    graph = null
    try {
      visGraph?.destroy()
    } catch {
      // ignore
    }
    visGraph = null

    if (renderEngine.value === 'vis') {
      visGraph = new VisKnowledgeGraph()
      visGraph.setTheme(themeMode.value as VisGraphTheme)
      void visGraph.init(
        { container: canvasRef.value! },
        props.nodes,
        props.edges,
        {
          editMode: currentEditMode.value,
          onAddNode: (x, y) => {
            ElMessageBox.prompt('请输入节点名称', '添加节点', {
              confirmButtonText: '确定',
              cancelButtonText: '取消',
              inputValue: '新节点'
            })
              .then((v: any) => {
                const name = String(v.value ?? '').trim()
                if (!name) return
                visGraph?.addNodeAt(x, y, name)
                editor?.markChanged()
              })
              .catch(() => {})
          },
          onProposeConnection: (sourceId: string, targetId: string) => {
            const a = visGraph?.getNodeDisplayLabel(sourceId) ?? sourceId
            const b = visGraph?.getNodeDisplayLabel(targetId) ?? targetId
            ElMessageBox.confirm(
              `是否在「${a}」与「${b}」之间建立关联？`,
              '建立关联',
              { confirmButtonText: '关联', cancelButtonText: '取消', type: 'info' }
            )
              .then(async () => {
                let label = '相关'
                try {
                  const r = await ElMessageBox.prompt('关系名称（可修改）', '关联', {
                    confirmButtonText: '确定',
                    cancelButtonText: '使用默认',
                    distinguishCancelAndClose: true,
                    inputValue: '相关'
                  })
                  label = String((r as { value: string }).value ?? '').trim() || '相关'
                } catch (e: unknown) {
                  if (e === 'cancel') label = '相关'
                  else return
                }
                visGraph?.addEdgeBetween(sourceId, targetId, label)
                editor?.markChanged()
              })
              .catch(() => {})
          },
          onDeleteNode: (nodeId) => {
            visGraph?.deleteNode(nodeId)
            editor?.markChanged()
          },
          onDeleteEdge: (edgeId) => {
            visGraph?.deleteEdge(edgeId)
            editor?.markChanged()
          },
          onDataChange: () => {
            editor?.markChanged()
          },
          onNodeContextMenu: (nodeId, label, color, menuX, menuY) => {
            nodeMenu.value = { visible: true, x: menuX, y: menuY, nodeId, label, color: color ?? '' }
            edgeMenu.value = { ...edgeMenu.value, visible: false }
          },
          onEdgeContextMenu: (edgeId, sourceId, targetId, label, menuX, menuY) => {
            edgeMenu.value = { visible: true, x: menuX, y: menuY, edgeId, sourceId, targetId, label }
            nodeMenu.value = { ...nodeMenu.value, visible: false }
          }
        }
      )
    } else {
      graph = new KnowledgeGraph()
      graph.setTheme(themeMode.value as G6GraphTheme)
      graph.init(
        {
          container: canvasRef.value!,
          width: props.width || width,
          height: props.height || height
        },
        props.nodes,
        props.edges,
        {
          editMode: currentEditMode.value,
          onAddNode: (x, y) => {
            ElMessageBox.prompt('请输入节点名称', '添加节点', {
              confirmButtonText: '确定',
              cancelButtonText: '取消',
              inputValue: '新节点'
            })
              .then((v: any) => {
                const name = String(v.value ?? '').trim()
                if (!name) return
                graph?.addNodeAt(x, y, name)
                editor?.markChanged()
              })
              .catch(() => {})
          },
          onProposeConnection: (sourceId: string, targetId: string) => {
            const a = graph?.getNodeDisplayLabel(sourceId) ?? sourceId
            const b = graph?.getNodeDisplayLabel(targetId) ?? targetId
            ElMessageBox.confirm(
              `是否在「${a}」与「${b}」之间建立关联？`,
              '建立关联',
              { confirmButtonText: '关联', cancelButtonText: '取消', type: 'info' }
            )
              .then(async () => {
                let label = '相关'
                try {
                  const r = await ElMessageBox.prompt('关系名称（可修改）', '关联', {
                    confirmButtonText: '确定',
                    cancelButtonText: '使用默认',
                    distinguishCancelAndClose: true,
                    inputValue: '相关'
                  })
                  label = String((r as { value: string }).value ?? '').trim() || '相关'
                } catch (e: unknown) {
                  if (e === 'cancel') label = '相关'
                  else return
                }
                graph?.addEdgeBetween(sourceId, targetId, label)
                editor?.markChanged()
              })
              .catch(() => {})
          },
          onDeleteNode: (nodeId) => {
            graph?.deleteNode(nodeId)
            editor?.markChanged()
          },
          onDeleteEdge: (edgeId) => {
            graph?.deleteEdge(edgeId)
            editor?.markChanged()
          },
          onDataChange: (_nodes, _edges) => {
            editor?.markChanged()
          },
          onNodeContextMenu: (nodeId, label, color, menuX, menuY) => {
            nodeMenu.value = { visible: true, x: menuX, y: menuY, nodeId, label, color: color ?? '' }
            edgeMenu.value = { ...edgeMenu.value, visible: false }
          },
          onEdgeContextMenu: (edgeId, sourceId, targetId, label, menuX, menuY) => {
            edgeMenu.value = { visible: true, x: menuX, y: menuY, edgeId, sourceId, targetId, label }
            nodeMenu.value = { ...nodeMenu.value, visible: false }
          }
        }
      )
    }

    // 注册图谱引用到 editor
    if (editor) {
      editor.setGraphRef({
        getEditData: () =>
          renderEngine.value === 'vis'
            ? (visGraph?.getEditData() ?? { nodes: [], edges: [] })
            : (graph?.getEditData() ?? { nodes: [], edges: [] }),
        applyEditData: (nodes: GraphNode[], edges: GraphEdge[]) =>
          renderEngine.value === 'vis'
            ? visGraph?.update(nodes, edges)
            : graph?.update(nodes, edges),
        promptAddNodeAtCenter: () => promptAddNodeAtCenter(),
        hintHowToAddRelation: () => hintHowToAddRelation()
      })
    }

    // 同步工具类型
    if (renderEngine.value === 'g6') {
      graph?.setToolType(currentToolType.value)
    } else if (renderEngine.value === 'vis') {
      visGraph?.setToolType(currentToolType.value as VisToolType)
    }
  })
}

const updateGraph = () => {
  if (renderEngine.value === 'vis') {
    if (!visGraph) {
      initGraph()
      return
    }
    visGraph.update(props.nodes, props.edges)
    return
  }

  if (!graph) {
    initGraph()
    return
  }
  graph.update(props.nodes, props.edges)
}

// 数据变化监听
watch(
  () => [props.nodes, props.edges],
  () => {
    updateGraph()
  },
  { deep: true }
)

// 引擎切换 → 重新初始化
watch(
  () => props.renderEngine,
  () => {
    nodeMenu.value.visible = false
    edgeMenu.value.visible = false
    initGraph()
  }
)

watch(
  () => props.theme,
  (v) => {
    const theme = (v ?? 'light') as 'light' | 'dark'
    graph?.setTheme(theme as G6GraphTheme)
    visGraph?.setTheme(theme as VisGraphTheme)
  }
)

// ========== 右键菜单处理 ==========

const handleRename = async () => {
  nodeMenu.value.visible = false
  const { nodeId, label } = nodeMenu.value
  try {
    const value = await ElMessageBox.prompt('请输入新的节点名称', '修名', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputValue: label
    }).then((v: any) => v.value as string)
    if (!value?.trim()) return
    if (renderEngine.value === 'vis') visGraph?.updateNodeLabel(nodeId, value.trim())
    else graph?.updateNodeLabel(nodeId, value.trim())
    editor?.markChanged()
  } catch {
    // 取消
  }
}

const handleChangeColor = async () => {
  nodeMenu.value.visible = false
  const { nodeId } = nodeMenu.value
  try {
    const color = await ElMessageBox.prompt('请选择节点颜色', '变更颜色', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputType: 'color',
      inputValue: nodeMenu.value.color || '#3b82f6',
      inputPattern: /^#([0-9a-fA-F]{6})$/,
      inputErrorMessage: '请选择有效颜色'
    }).then((v: any) => v.value as string)
    if (renderEngine.value === 'vis') {
      visGraph?.updateNodeColor(nodeId, color)
    } else {
      graph?.updateNodeColor(nodeId, color)
    }
    editor?.markChanged()
  } catch {
    // 取消
  }
}

const handleMenuCreateLink = () => {
  const { nodeId } = nodeMenu.value
  nodeMenu.value.visible = false
  if (!currentEditMode.value) return
  if (renderEngine.value === 'vis') {
    if (!visGraph) return
    visGraph.startConnectFrom(nodeId)
    ElMessage.info('请点击目标节点建立连接')
    return
  }
  if (!graph) return
  graph.startConnectFrom(nodeId)
  ElMessage.info('请点击目标节点，或按住 Alt 从节点拖线到目标')
}

const handleMenuDeleteNode = () => {
  nodeMenu.value.visible = false
  const { nodeId } = nodeMenu.value
  ElMessageBox.confirm('确定要删除该节点吗？', '删除节点', {
    confirmButtonText: '确定删除',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    if (renderEngine.value === 'vis') visGraph?.deleteNode(nodeId)
    else graph?.deleteNode(nodeId)
    editor?.markChanged()
  }).catch(() => {})
}

const handleEditEdgeLabel = async () => {
  edgeMenu.value.visible = false
  const { edgeId, label } = edgeMenu.value
  try {
    const value = await ElMessageBox.prompt('请输入新的关系名称', '改关系', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputValue: label
    }).then((v: any) => v.value as string)
    if (renderEngine.value === 'vis') visGraph?.updateEdge(edgeId, { label: value })
    else graph?.updateEdge(edgeId, { label: value })
    editor?.markChanged()
  } catch {
    // 取消
  }
}

const handleReverseEdge = () => {
  edgeMenu.value.visible = false
  const { edgeId, sourceId, targetId } = edgeMenu.value
  ElMessageBox.confirm(
    `确定反转该关系的方向吗？\n\n当前：节点A → 节点B\n反转后：节点B → 节点A`,
    '反转关系',
    { confirmButtonText: '确定反转', cancelButtonText: '取消', type: 'warning' }
  ).then(() => {
    if (renderEngine.value === 'vis') visGraph?.updateEdge(edgeId, { source: targetId, target: sourceId })
    else graph?.updateEdge(edgeId, { source: targetId, target: sourceId })
    editor?.markChanged()
  }).catch(() => {})
}

const handleMenuDeleteEdge = () => {
  edgeMenu.value.visible = false
  const { edgeId } = edgeMenu.value
  ElMessageBox.confirm('确定要删除该关系吗？', '删除关系', {
    confirmButtonText: '确定删除',
    cancelButtonText: '取消',
    type: 'warning'
  }).then(() => {
    if (renderEngine.value === 'vis') visGraph?.deleteEdge(edgeId)
    else graph?.deleteEdge(edgeId)
    editor?.markChanged()
  }).catch(() => {})
}

// 点击画布空白处关闭菜单
const handleCanvasClick = () => {
  nodeMenu.value.visible = false
  edgeMenu.value.visible = false
}

const fitView = () => {
  if (renderEngine.value === 'vis') {
    visGraph?.fitView()
    return
  }
  graph?.fitView(20)
}
const resetLayout = () => {
  if (renderEngine.value === 'vis') {
    visGraph?.fitView()
    return
  }
  graph?.resetLayout()
}
const switchLayout = (type: 'force' | 'circular') => {
  if (renderEngine.value === 'vis') return
  graph?.switchLayout(type)
}
const getLayoutType = () => graph?.getLayoutType() ?? 'force'

const focusNode = (nodeId: string) => {
  if (renderEngine.value === 'vis') {
    visGraph?.focusNode(nodeId)
    return
  }
  if (graph) {
    const g = (graph as any).graph
    if (g) {
      const node = g.findById(nodeId)
      if (node) {
        g.focusItem(node, true, { easing: 'easeCubic', duration: 500 })
      }
    }
  }
}

const getEditData = () =>
  renderEngine.value === 'vis'
    ? (visGraph?.getEditData() ?? { nodes: [], edges: [] })
    : (graph?.getEditData() ?? { nodes: [], edges: [] })
const applyEditData = (nodes: GraphNode[], edges: GraphEdge[]) =>
  renderEngine.value === 'vis'
    ? visGraph?.update(nodes, edges)
    : graph?.update(nodes, edges)
const updateNodeLabel = (nodeId: string, newLabel: string) => {
  if (renderEngine.value === 'vis') visGraph?.updateNodeLabel(nodeId, newLabel)
  else graph?.updateNodeLabel(nodeId, newLabel)
  editor?.markChanged()
}
const updateEdge = (edgeId: string, updates: { label?: string; source?: string; target?: string }) => {
  if (renderEngine.value === 'vis') visGraph?.updateEdge(edgeId, updates)
  else graph?.updateEdge(edgeId, updates)
  editor?.markChanged()
}
const deleteNode = (nodeId: string) => {
  if (renderEngine.value === 'vis') visGraph?.deleteNode(nodeId)
  else graph?.deleteNode(nodeId)
  editor?.markChanged()
}
const deleteEdge = (edgeId: string) => {
  if (renderEngine.value === 'vis') visGraph?.deleteEdge(edgeId)
  else graph?.deleteEdge(edgeId)
  editor?.markChanged()
}

/** 工具栏「添加节点」：在视口中心弹出命名并创建 */
const promptAddNodeAtCenter = async () => {
  if (renderEngine.value === 'vis' && !visGraph) return
  if (renderEngine.value === 'g6' && !graph) return
  if (!currentEditMode.value) {
    ElMessage.warning('请先进入编辑模式')
    return
  }
  const pt = renderEngine.value === 'vis'
    ? visGraph?.getViewportCenterGraphPoint()
    : graph?.getViewportCenterGraphPoint()
  if (!pt) return
  try {
    const r = await ElMessageBox.prompt('请输入节点名称', '添加节点', {
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputValue: '新节点'
    })
    const name = String((r as { value: string }).value ?? '').trim()
    if (!name) return
    if (renderEngine.value === 'vis') visGraph?.addNodeAt(pt.x, pt.y, name)
    else graph?.addNodeAt(pt.x, pt.y, name)
    editor?.markChanged()
  } catch {
    /* 取消 */
  }
}

const hintHowToAddRelation = () => {
  if (renderEngine.value === 'vis') {
    ElMessage.info('添加关系：依次点击两个节点建立连接')
    return
  }
  ElMessage.info('添加关系：依次点击两个节点；或右键选「创建连接」；或按住 Alt 从节点拖线到目标')
}

// ============================================================
// 工具栏事件处理（由父组件的 GraphToolbar 触发）
// ============================================================

/** 点击工具栏「编辑」按钮：进入编辑模式 */
const handleToggleEdit = () => {
  if (editor) {
    editor.enterEditMode(
      JSON.parse(JSON.stringify(props.nodes)),
      JSON.parse(JSON.stringify(props.edges))
    )
  } else {
    emit('update:editMode', true)
  }
}

/** 点击工具栏「保存」按钮 */
const handleSave = () => {
  if (editor) {
    editor.save()
  } else {
    emit('save')
  }
}

/** 点击工具栏「取消」按钮 */
const handleCancel = () => {
  if (editor) {
    emit('cancel')
  } else {
    emit('cancel')
  }
}

const handleSwitchToForce = () => {
  graph?.switchLayout('force')
}

const handleSwitchToCircular = () => {
  graph?.switchLayout('circular')
}

const onToolChange = (tool: any) => {
  // 工具切换时提示
}

// ============================================================
// Expose（供父组件通过 template ref 访问）
// ============================================================

defineExpose({
  fitView,
  resetLayout,
  switchLayout,
  getLayoutType,
  focusNode,
  getEditData,
  applyEditData,
  updateNodeLabel,
  updateEdge,
  deleteNode,
  deleteEdge,
  promptAddNodeAtCenter,
  hintHowToAddRelation,
  setToolType: (type: ToolType) => { currentToolType.value = type },
  // 工具栏事件处理（供父组件在 template 中调用）
  handleToggleEdit,
  handleSave,
  handleCancel,
  handleSwitchToForce,
  handleSwitchToCircular,
  // 内部编辑器状态（供父组件按需读取）
  isEditing: currentEditMode,
  hasChanges: currentHasChanges,
  // 内部编辑器的核心方法（取消确认后由视图调用）
  save: editor?.save ?? null,
  cancelEdit: editor?.cancelEdit ?? null
})

onMounted(() => {
  if (props.nodes.length > 0 || props.edges.length > 0) {
    initGraph()
  }

  resizeObserver = new ResizeObserver((entries) => {
    for (const entry of entries) {
      const { width, height } = entry.contentRect
      if (resizeTimeout) clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        if (width > 0 && height > 0 && renderEngine.value === 'g6' && graph) {
          const g = (graph as any).graph
          if (g && (g.getWidth() !== width || g.getHeight() !== height)) {
            g.changeSize(width, height)
          }
        } else if (width > 0 && height > 0 && renderEngine.value === 'vis' && visGraph) {
          visGraph.fitView()
        } else if (width > 0 && height > 0) {
          initGraph()
        }
      }, 100)
    }
  })

  if (containerRef.value) {
    resizeObserver.observe(containerRef.value)
    if (props.nodes.length > 0 || props.edges.length > 0) {
      setTimeout(() => { if (!graph) initGraph() }, 100)
    }
  }
})

onUnmounted(() => {
  if (resizeTimeout) clearTimeout(resizeTimeout)
  resizeObserver?.disconnect()
  resizeObserver = null
  editor?.setGraphRef(null)
  graph?.destroy()
  graph = null
  visGraph?.destroy()
  visGraph = null
})
</script>

<style scoped>
.graph-container {
  width: 100%;
  height: 100%;
  position: relative;
  background: #f8fafc;
  transition: background-color 0.2s ease;
}

.graph-canvas {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  background: transparent;
}

.context-menu,
:deep(.kg-editor-toolbar) {
  z-index: 1000;
}

.graph-container.edit-mode {
  border: 2px dashed #3b82f6;
  border-radius: 4px;
}

.graph-container.theme-dark {
  background: #0f172a;
}

.graph-container.theme-dark.edit-mode {
  border-color: #60a5fa;
}

/* 右键菜单 */
.context-menu {
  position: absolute;
  z-index: 1000;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  padding: 6px 0;
  min-width: 140px;
  user-select: none;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 14px;
  font-size: 13px;
  color: #333;
  cursor: pointer;
  transition: background 0.15s;
}

.context-menu-item:hover {
  background: #f0f4ff;
  color: #3b82f6;
}

.context-menu-item.danger {
  color: #ff4d4f;
}

.context-menu-item.danger:hover {
  background: #fff1f0;
  color: #ff4d4f;
}

.context-menu-item svg {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.graph-container.theme-dark .context-menu {
  background: #111827;
  border-color: #334155;
  box-shadow: 0 10px 24px rgba(2, 6, 23, 0.45);
}

.graph-container.theme-dark .context-menu-item {
  color: #cbd5e1;
}

.graph-container.theme-dark .context-menu-item:hover {
  background: #1e293b;
  color: #93c5fd;
}

.graph-container.theme-dark .context-menu-item.danger {
  color: #fca5a5;
}

.graph-container.theme-dark .context-menu-item.danger:hover {
  background: rgba(239, 68, 68, 0.16);
  color: #fecaca;
}
</style>
